import { Component, effect, inject, signal } from '@angular/core';
import { ExportTypeEnum } from "@models/common/export-type.enum";
import { ColumnWithExportName, ExtendedColGroupDef } from "@models/grid/column-with-export-name";
import { ClientSecurityTransaction } from "@models/security-transactions/client-security-transaction";
import { ShareGroupView } from "@pages/shares/models/share-group-view";
import { SharesExportService } from "@pages/shares/services/shares-export.service";
import {
  ListGridClientSideComponent
} from "@shared/components/grid/components/list-grid-client-side/list-grid-client-side.component";
import { SharesGridService } from "@pages/shares/services/shares-grid.service";
import { ICellRendererAngularComp } from "ag-grid-angular";
import { GridApi, ICellRendererParams, RowClassParams, RowClassRules, } from "ag-grid-community";
import { ColDef, GridOptions } from "ag-grid-enterprise";
import { AgShareGroupButtonsComponent } from "../ag-share-group-buttons/ag-share-group-buttons.component";

type ActionEvent = (data?: ShareGroupView) => void;

export interface IAgShareGroupCellRenderParams {
  showUnlodgedChanges: boolean,
  exportShares: ActionEvent,
  resolveIssue: ActionEvent,
  issueTransaction: ActionEvent,
  transferTransaction: ActionEvent,
  cancelTransaction: ActionEvent,
  subdivisionOrConsolidationTransaction: ActionEvent,
  conversionTransaction: ActionEvent,
  changeAmountPaidTransaction: ActionEvent,
  addHistoryTransaction: ActionEvent,
  hasIssue: boolean,
  detailGridOptions: GridOptions
}

type CellRendererParams = ICellRendererParams<ShareGroupView> & IAgShareGroupCellRenderParams;

@Component({
  selector: 'app-ag-share-group',
  standalone: true,
  imports: [ListGridClientSideComponent],
  templateUrl: 'ag-share-group.component.html',
  styleUrl: 'ag-share-group.component.scss'
})
export class AgShareGroupComponent implements ICellRendererAngularComp {
  private sharesService = inject(SharesGridService);
  private sharesExportService = inject(SharesExportService);

  isCompanyLoading = this.sharesService.isCompanyLoading;

  params!: CellRendererParams;
  hasIssue = false;
  rowData: unknown[] = [];

  constructor() {
    effect(() => this.statusBar = this.getStatusBar(this.isCompanyLoading()));
  }

  agInit(params: CellRendererParams): void {
    this.updateGroup(params);
  }

  refresh(params: CellRendererParams): boolean {
    return this.updateGroup(params);
  }

  updateGroup(params: CellRendererParams): boolean {
    this.params = params;
    this.hasIssue = params.data?.hasIssue ?? false;
    this.statusBar.statusPanels[0].statusPanelParams.hasIssue = this.hasIssue;
    this.columnDefs = this.params.detailGridOptions.columnDefs as ColDef[];
    this.rowData = params.data?.callRecords ?? [];

    this.colDefs.set(this.columnDefs);

    const columnDef: ColDef | undefined = this.columnDefs.find((col: ColDef) => col.field === 'transactionDate');

    if (columnDef) {
      columnDef.checkboxSelection = false;
      columnDef.headerCheckboxSelection = false;
    }

    return true;
  }

  onGridReady(gridApi: GridApi): void {
    this.sharesService.gridApi = gridApi;
    const autoSizeColumnList = this.sharesService.autoSizeColumnList;
    gridApi.autoSizeColumns(autoSizeColumnList);
  }

  resolveIssue(): void {
  }

  issueTransaction(): void {
    this.params.issueTransaction(this.params.data);
  }

  transferTransaction(): void {
    this.params.transferTransaction(this.params.data);
  }

  cancelTransaction(): void {
    this.params.cancelTransaction(this.params.data);
  }

  subdivisionOrConsolidationTransaction(): void {
    this.params.subdivisionOrConsolidationTransaction(this.params.data);
  }

  conversionTransaction(): void {
    this.params.conversionTransaction(this.params.data);
  }

  changeAmountPaidTransaction(): void {
    this.params.changeAmountPaidTransaction(this.params.data);
  }

  addHistoryTransaction(): void {
    this.params.addHistoryTransaction(this.params.data);
  }

  isTransactionAlert(params: RowClassParams<ClientSecurityTransaction>): boolean {
    return params.data?.isIncludeAlert ?? false;
  }

  isTransactionWarning(params: RowClassParams<ClientSecurityTransaction>): boolean {
    return params.data?.isIncludeWarning ?? false;
  }

  // =====  Grid  =====
  readonly paginationPageSize = 20;
  readonly rowClassRules: RowClassRules = {
    'non-lodged-data': (params: RowClassParams<ClientSecurityTransaction>) => !params.data?.isPosted,
    'transaction-alert': this.isTransactionAlert.bind(this),
    'transaction-warning': this.isTransactionWarning.bind(this),
  };

  colDefs = signal<(ColumnWithExportName | ExtendedColGroupDef)[]>([]);

  columnDefs: ColDef[] = [];
  statusBar = this.getStatusBar(false);

  getStatusBar(isCompanyLoading: boolean) {
    return {
      statusPanels: [
        {
          statusPanel: AgShareGroupButtonsComponent,
          statusPanelParams: {
            hasIssue: false,
            isCompanyLoading: isCompanyLoading,
            exportShares: this.exportShares.bind(this),
            resolveIssue: this.resolveIssue.bind(this),
            issueTransaction: this.issueTransaction.bind(this),
            transferTransaction: this.transferTransaction.bind(this),
            cancelTransaction: this.cancelTransaction.bind(this),
            subdivisionOrConsolidationTransaction: this.subdivisionOrConsolidationTransaction.bind(this),
            conversionTransaction: this.conversionTransaction.bind(this),
            changeAmountPaidTransaction: this.changeAmountPaidTransaction.bind(this),
            addHistoryTransaction: this.addHistoryTransaction.bind(this),
          },
          align: 'left'
        },
      ]
    };
  }

  // =====  Export  =====

  readonly excelExportParams = this.sharesExportService.exportParamsXls();
  readonly csvExportParams = this.sharesExportService.exportParamsCsv();

  exportShares(exportType: ExportTypeEnum): void {
    this.exportSharesList(false, exportType);
  }

  exportSharesList(isBulkExport: boolean, exportType: ExportTypeEnum): void {
    this.sharesExportService.exportSharesListInGroup(
      this.sharesService.gridApi,
      this.colDefs(),
      this.rowData,
      exportType
    );
  }
}
